import { zodResolver } from '@hookform/resolvers/zod';
import { Password } from '@horevia/contracts';
import { Button, FormField, Input, Notice, PasswordInput, Skeleton } from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Lock, User as UserIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { acceptInvitation, invitationQueryOptions } from '@/features/auth/queries';
import { errorMessage } from '@/lib/errors';

export const Route = createFileRoute('/invite/$token')({ component: AcceptInvitationPage });

const FormSchema = z
  .object({
    fullName: z.string().min(2).max(120),
    password: Password,
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'mismatch',
  });
type FormValues = z.infer<typeof FormSchema>;

function AcceptInvitationPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { token } = Route.useParams();
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const invitation = useQuery(invitationQueryOptions(token));

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { fullName: '', password: '', confirm: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (invitation.data?.fullName && !form.getValues('fullName')) {
      form.setValue('fullName', invitation.data.fullName);
    }
  }, [invitation.data?.fullName, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await acceptInvitation({ token, fullName: values.fullName, password: values.password });
      setDone(true);
    } catch (error) {
      setFormError(errorMessage(error, t));
    }
  });

  if (done) {
    return (
      <AuthLayout title={t('auth:invitation.successTitle')}>
        <div className="stack">
          <Notice tone="success">{t('auth:invitation.successBody')}</Notice>
          <Button asChild variant="primary" block>
            <Link to="/login">{t('auth:login.submit')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (invitation.isPending) {
    return (
      <AuthLayout title={t('auth:invitation.title')}>
        <div className="stack" aria-busy="true">
          <Skeleton variant="line" width="70%" />
          <Skeleton height={38} />
          <Skeleton height={38} />
          <Skeleton height={46} />
        </div>
      </AuthLayout>
    );
  }

  if (invitation.isError || !invitation.data) {
    return (
      <AuthLayout title={t('auth:invitation.title')}>
        <div className="stack">
          <Notice tone="danger" role="alert">
            {t('auth:invitation.invalid')}
          </Notice>
          <Button asChild variant="secondary" block>
            <Link to="/login">{t('auth:forgot.backToLogin')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth:invitation.title')}
      subtitle={t('auth:invitation.subtitle', {
        tenant: invitation.data.tenantName,
        roles: invitation.data.roleNames.join(', '),
      })}
    >
      <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
        {formError ? (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        ) : null}

        <FormField label={t('common:fields.email')}>
          {(field) => (
            <Input
              id={field.id}
              value={invitation.data.email}
              readOnly
              disabled
              autoComplete="username"
            />
          )}
        </FormField>

        <FormField
          label={t('auth:invitation.fullName')}
          error={form.formState.errors.fullName ? t('common:validation.required') : undefined}
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <Input
              id={field.id}
              icon={<UserIcon className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              {...form.register('fullName')}
            />
          )}
        </FormField>

        <FormField
          label={t('auth:invitation.password')}
          hint={t('common:validation.passwordPolicy')}
          error={form.formState.errors.password ? t('common:validation.passwordPolicy') : undefined}
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <PasswordInput
              id={field.id}
              autoComplete="new-password"
              icon={<Lock className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              showLabel={t('common:actions.showPassword')}
              hideLabel={t('common:actions.hidePassword')}
              {...form.register('password')}
            />
          )}
        </FormField>

        <FormField
          label={t('auth:invitation.confirm')}
          error={
            form.formState.errors.confirm ? t('common:validation.passwordMismatch') : undefined
          }
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <PasswordInput
              id={field.id}
              autoComplete="new-password"
              icon={<Lock className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              showLabel={t('common:actions.showPassword')}
              hideLabel={t('common:actions.hidePassword')}
              {...form.register('confirm')}
            />
          )}
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={form.formState.isSubmitting}
        >
          {t('auth:invitation.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
